import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {dropdownIndicatorsOpen, setCentreMessage, toggleIncidentType, toggleOverlay} from '../actions';
import Combo from '../components/Combo';
import {MODES} from '../Constants';
import {updateIndicatorOrchestrator} from '../orchestrators';
import './Navigator.css';

class IndicatorNavigator extends React.Component {

  state = {
    burnSectionsExpanded: {
      FFMVIC: true,
      CFA: true
    }
  };

  isHotPotIndicator = (layer) => {
    return layer.prefix.startsWith('HP_') || layer.prefix.startsWith('Generated_');
  };

  handleToggle = () => {
    const { dispatch, list_open } = this.props;
    dispatch(dropdownIndicatorsOpen(!list_open));
  };

  onIndicatorClick = (indicator) => {
    const { dispatch } = this.props;

    if (indicator.available) {
      dispatch(updateIndicatorOrchestrator(indicator));
    }
  };

  onIndicatorHelp = (indicator, event) => {
    const moreInfoLink = indicator.moreInfoLink || 'https://foresight.help.ffm.vic.gov.au/indicator-description';
    const { dispatch } = this.props;
    event.stopPropagation();
    event.preventDefault();
    dispatch(setCentreMessage(<span>
        <span dangerouslySetInnerHTML={{ __html: indicator.description.replaceAll('\n', '<br/>') }}/>
        &nbsp; <a href={moreInfoLink} target="ind_desc">More info.</a></span>,
      indicator.expandedAbbreviation || indicator.displayName));
  };

  onOverlayClick = (overlay) => {
    const { dispatch } = this.props;
    dispatch(toggleOverlay(overlay.id));
  };

  onIncidentTypeClick = (incidentType) => {
    const { dispatch } = this.props;
    dispatch(toggleIncidentType(incidentType.id));
  };

  onBurnSectionExpandToggle = (burnAgencyKey) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState(s => ({
      burnSectionsExpanded: {
        ...s.burnSectionsExpanded,
        [burnAgencyKey]: !s.burnSectionsExpanded[burnAgencyKey]
      }
    }));
  };

  setBurnLayersActive = (layers, wantActive) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const { dispatch } = this.props;
    layers.forEach(l => {
      if (l.active !== wantActive) {
        dispatch(toggleIncidentType(l.id));
      }
    });
  };

  toGroups = (indicators) => {
    const { app_mode } = this.props;
    const filteredIndicators = indicators
    .filter(e => !e.hide)
    .filter(e => !e.only_in_app_mode || e.only_in_app_mode === app_mode);
    return _.groupBy(filteredIndicators, 'group');
  };

  renderIndicator = (indicator, i) => {
    return (
      <div className={'c-navigator__option ' + (indicator.available ? 'is-available' : 'is-unavailable')}
           key={'indicator-toggle-' + i}>
        <input className="c-navigator__input" type="radio" checked={indicator.active}
               readOnly={true} id={'indicator-toggle-' + i}/>
        <label
          className="c-navigator__label"
          htmlFor={'indicator-toggle-' + i}
          onClick={this.onIndicatorClick.bind(null, indicator)}>
          <abbr title={indicator.expandedAbbreviation}>
            {indicator.displayName}
          </abbr>
        </label>
        &nbsp;
        {this.isHotPotIndicator(indicator) && <span className="c-navigator__hotpot-star">^</span>}
        {indicator.description && <span className="c-navigator__help"
                                        onClick={this.onIndicatorHelp.bind(null, indicator)}>
                    <i className="fa fa-question-circle"/></span>}
      </div>
    );
  };

  renderOverlay = (overlay, i) => {
    return (
      <div className={'c-navigator__option is-available'}
           key={'overlay-toggle-' + i}>
        <input className="c-navigator__input" type="checkbox" checked={overlay.active}
               readOnly={true} id={'overlay-toggle-' + i}/>
        <label
          className="c-navigator__label"
          htmlFor={'overlay-toggle-' + i}
          onClick={this.onOverlayClick.bind(null, overlay)}>
          <abbr title={overlay.expandedAbbreviation}>
            {overlay.displayName}
            &nbsp;
            {overlay.id.startsWith('HP_') && <span className="c-navigator__hotpot-star">^</span>}
          </abbr>
        </label>
        {overlay.icon && <i className={'icon ' + overlay.icon}/>}
        {overlay.description && <span className="c-navigator__help"
                                      onClick={this.onIndicatorHelp.bind(null, overlay)}>
            <i className="fa fa-question-circle"/></span>}
      </div>
    );
  };

  renderIncidents = (incidentType, nestedClass) => {
    const extra = typeof nestedClass === 'string' && nestedClass
      ? ' ' + nestedClass
      : '';
    return (
      <div className={'c-navigator__option is-available' + extra}
           key={'incident-toggle-' + incidentType.id}>
        <input className="c-navigator__input" type="checkbox" checked={incidentType.active}
               readOnly={true} id={'incident-toggle-' + incidentType.id}/>
        <label
          className="c-navigator__label"
          htmlFor={'incident-toggle-' + incidentType.id}
          onClick={this.onIncidentTypeClick.bind(null, incidentType)}>
          <abbr title={incidentType.expandedAbbreviation}>
            {incidentType.displayName}
          </abbr>
        </label>
        {incidentType.description && <span className="c-navigator__help"
                                           onClick={this.onIndicatorHelp.bind(null, incidentType)}>
            <i className="fa fa-question-circle"/></span>}
      </div>
    );
  };

  sortIncidentRest = (items) => {
    const order = { INCIDENT_PRESCRIPTIONS: 0, BUSHFIRES: 1, INCIDENT_POLYGONS: 2 };
    return _.sortBy(items, [
      r => (order[r.id] != null ? order[r.id] : 99),
      'displayName'
    ]);
  };

  renderIncidentsBlock = () => {
    const { incidents, app_mode } = this.props;
    const visible = incidents.filter(e => !e.only_in_app_mode || e.only_in_app_mode === app_mode);
    if (app_mode !== MODES.PLANNED_BURNS) {
      return _.sortBy(visible, ['menu_order_override', 'displayName']).map(this.renderIncidents);
    }
    const ffmvicBurns = _.sortBy(
      visible.filter(i => i.burnAgency === 'FFMVIC'),
      ['menu_order_override', 'displayName']
    );
    const cfaBurns = _.sortBy(
      visible.filter(i => i.burnAgency === 'CFA'),
      ['menu_order_override', 'displayName']
    );
    const rest = this.sortIncidentRest(visible.filter(i => !i.burnAgency));
    const { burnSectionsExpanded } = this.state;
    const nested = 'c-navigator__option--burn-nested';
    return (
      <React.Fragment>
        <div className="c-navigator__burn-group">
          <div className="c-navigator__burn-group-header">
            <button type="button" className="c-navigator__burn-group-toggle"
                    onClick={this.onBurnSectionExpandToggle('FFMVIC')}
                    aria-expanded={burnSectionsExpanded.FFMVIC}
                    aria-controls="burn-section-ffmvic">
              <span className="c-navigator__burn-group-chevron" aria-hidden="true">
                {burnSectionsExpanded.FFMVIC ? '\u25BC' : '\u25B6'}
              </span>
              FFMVic Burns
            </button>
            <span className="c-navigator__burn-group-actions">
              <button type="button" className="c-navigator__burn-group-link"
                      onClick={this.setBurnLayersActive(ffmvicBurns, true)}>All on</button>
              <span className="c-navigator__burn-group-sep">|</span>
              <button type="button" className="c-navigator__burn-group-link"
                      onClick={this.setBurnLayersActive(ffmvicBurns, false)}>All off</button>
            </span>
          </div>
          <div id="burn-section-ffmvic" className="c-navigator__burn-group-body">
            {burnSectionsExpanded.FFMVIC && ffmvicBurns.map(i => this.renderIncidents(i, nested))}
          </div>
        </div>
        <div className="c-navigator__burn-group">
          <div className="c-navigator__burn-group-header">
            <button type="button" className="c-navigator__burn-group-toggle"
                    onClick={this.onBurnSectionExpandToggle('CFA')}
                    aria-expanded={burnSectionsExpanded.CFA}
                    aria-controls="burn-section-cfa">
              <span className="c-navigator__burn-group-chevron" aria-hidden="true">
                {burnSectionsExpanded.CFA ? '\u25BC' : '\u25B6'}
              </span>
              CFA Burns
            </button>
            <span className="c-navigator__burn-group-actions">
              <button type="button" className="c-navigator__burn-group-link"
                      onClick={this.setBurnLayersActive(cfaBurns, true)}>All on</button>
              <span className="c-navigator__burn-group-sep">|</span>
              <button type="button" className="c-navigator__burn-group-link"
                      onClick={this.setBurnLayersActive(cfaBurns, false)}>All off</button>
            </span>
          </div>
          <div id="burn-section-cfa" className="c-navigator__burn-group-body">
            {burnSectionsExpanded.CFA && cfaBurns.map(i => this.renderIncidents(i, nested))}
          </div>
        </div>
        {rest.map(this.renderIncidents)}
      </React.Fragment>
    );
  };

  renderGroup = (group, indicators) => {
    const underscore_idx = group.indexOf('_');
    if (underscore_idx > 0) {
      group = group.substring(underscore_idx + 1);
    }
    return (<div key={group}>
      <div className="c-navigator__break"><span>{group}</span></div>
      {_.sortBy(indicators, ['menu_order_override', 'displayName']).map(this.renderIndicator)}
    </div>);
  };

  render() {
    const { indicators, overlays, list_open } = this.props;
    const indicatorGroups = this.toGroups(indicators);
    const groups = _.keys(indicatorGroups).sort();

    return (
      <Combo open={list_open} label="Indicators" onClick={this.handleToggle}>
        <div className="c-navigator__fban-legend"><span className="c-navigator__hotpot-star">^</span> HotPot Data</div>
        {
          groups.map((g) => this.renderGroup(g, indicatorGroups[g]))
        }
        <div className="c-navigator__break"><span>Weather Overlays</span></div>
        {
          _.sortBy(overlays, ['menu_order_override', 'displayName']).filter(e => !e.hide).map(this.renderOverlay)
        }
        <div className="c-navigator__break"><span>Incidents</span></div>
        {this.renderIncidentsBlock()}
      </Combo>
    );
  }
}

const mapStateToProps = state => {
  return {
    indicators: state.indicators.indicators,
    overlays: state.overlays.layers,
    list_open: state.view.indicators_list_open,
    incidents: state.incidents.layers,
    app_mode: state.app.app_mode
  };
};

const mapDispatchToProps = dispatch => {
  return {
    dispatch: dispatch
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(IndicatorNavigator);



// WEBPACK FOOTER //
// ./src/containers/IndicatorNavigator.js